import { ReactElement } from 'react';
import { WidgetId } from 'types/idTypes';
import { Subset, ValueOf } from 'types/object.types';
import { Widget, WidgetType } from 'types/widget.types';

import { BasicShapeWidgetData } from './BasicShapeWidget/BasicShapeWidget.types';
import { BarChartWidgetData } from './ChartWidgets/BarChartWidget/BarChartWidget.types';
import { ColumnChartWidgetData } from './ChartWidgets/ColumnChartWidget/ColumnChartWidget.types';
import { LineChartWidgetData } from './ChartWidgets/LineChartWidget/LineChartWidget.types';
import { PieChartWidgetData } from './ChartWidgets/PieChartWidget/PieChartWidget.types';
import { GroupWidgetData } from './GroupWidget/GroupWidget.types';
import { IconWidgetData } from './IconWidget/IconWidget.types';
import { ImageWidgetData } from './ImageWidget/ImageWidget.types';
import { LineWidgetData } from './LineWidget/LineWidget.types';
import { MapWidgetData } from './MapWidget/MapWidget.types';
import { ProgressChartWidgetData } from './ProgressChartWidget/ProgressChartWidget.types';
import { QRCodeWidgetData } from './QRCodeWidget/QRCodeWidget.types';
import { StatChartWidgetData } from './ResponsiveWidgets/StatChartWidget/StatChartWidget.types';
import { LabelTextWidgetData } from './TextBasedWidgets/LabelTextWidget/LabelTextWidget.types';
import { TableWidgetData } from './TextBasedWidgets/TableWidget/TableWidget.types';
import { TextWidgetData } from './TextBasedWidgets/TextWidget/TextWidget.types';

type NewWidget = {
  widgetType: WidgetType;
  widgetData: AllWidgetData;
  groupWidgets?: NewWidget[];

  // For widgets that extend ResponsiveWidgetData
  isResponsiveGroup?: boolean;
  componentKey?: string;
  copyFrom?: WidgetId;

  // custom widget id - if you need to know the widgetID before adding the widget to the store
  customWidgetId?: WidgetId;
};

type SetWidgetRef = (id: WidgetId, ref: HTMLElement | null) => void;
type CleanupWidgetBoundingBoxConfig = (id: WidgetId) => void;
type SetCustomWidgetOverride = (
  id: WidgetId,
  boundingBoxFunction: (arg: any) => void,
  boundingBoxFunctionName: string,
) => void;

/**
 * Convenience type which could be any of defined widget data type for all widgets
 */
type AllWidgetData =
  | IconWidgetData
  | TextWidgetData
  | GroupWidgetData
  | LineWidgetData
  | BasicShapeWidgetData
  | ImageWidgetData
  | PieChartWidgetData
  | LineChartWidgetData
  | TableWidgetData
  | ColumnChartWidgetData
  | BarChartWidgetData
  | ProgressChartWidgetData
  | StatChartWidgetData
  | LabelTextWidgetData
  | QRCodeWidgetData
  | MapWidgetData;

type WidgetMap = {
  [index: WidgetId]: Widget | AllWidgetData;
};

/**
 * Widget font property name has to be one of following standard enum value
 */
enum WidgetFontProperty {
  family = 'family',
  fontFamily = 'fontFamily',
}

type GetWidgetMemberComponent = () => (args: {
  widgetId: WidgetId;
  zIndex?: number;
  customWidgetData?: Partial<Widget | AllWidgetData>;
  isReadOnly?: boolean;
  includeAltTextImg?: boolean;
  getWidgetMemberComponent?: GetWidgetMemberComponent;
}) => ReactElement | null;

// All widget property values that are supported for multi widget editing
enum MultiEditOption {
  Color = 'color',
  TextColor = 'textColor',
  FontSize = 'fontSize',
  FontFamily = 'fontFamily',
  FontWeight = 'fontWeight',
  FontStyle = 'fontStyle',
  TextDecoration = 'textDecoration',
  TextAlign = 'textAlign',
  BorderColor = 'borderColor',
  BorderStyle = 'borderStyle',
  BorderWidth = 'borderWidth',
}

// All widget property change types that are supported for multi widget editing
enum MultiEditChangeOption {
  OnChange = 'onChange',
  OnIncrement = 'onIncrement',
  OnDecrement = 'onDecrement',
}

enum ReadOnlyRenderType {
  ThumbnailReadOnly = 'thumbnail-readonly',
  StructrueTreeList = 'structureTreeList',
  ExportStructureTreeList = 'exportStructureTreeList',
}

type OnMultiEditChange<WidgetData> = (args: { value: any; widgetData: WidgetData }) => Subset<WidgetData>;

// All widget types that are supported for multi widget editing
type MultiEditWidgetTypes = WidgetType.Icon | WidgetType.Text | WidgetType.Line | WidgetType.BasicShape;

// Custom widget config for multi widget editing
// Widgets can optionally override onChange, onIncrement, onDecrement for each supported property
// Widgets can optionally override key and getValue for each supported property
type MultiEditWidgetConfig<WidgetData> = {
  [Option in MultiEditOption]?: {
    [MultiEditChangeOption.OnChange]?: OnMultiEditChange<WidgetData & Widget>;
    [MultiEditChangeOption.OnIncrement]?: OnMultiEditChange<WidgetData & Widget>;
    [MultiEditChangeOption.OnDecrement]?: OnMultiEditChange<WidgetData & Widget>;
    key?: string;
    getValue?: (widgetData: WidgetData & Widget) => ValueOf<WidgetData & Widget & string>;
  };
} & {
  // optionally show/hide multi select option
  displayMultiSelect?: () => boolean;
};

export type {
  WidgetMap,
  SetWidgetRef,
  CleanupWidgetBoundingBoxConfig,
  SetCustomWidgetOverride,
  AllWidgetData,
  NewWidget,
  GroupWidgetData,
  GetWidgetMemberComponent,
  MultiEditWidgetConfig,
  MultiEditWidgetTypes,
  OnMultiEditChange,
};

export { WidgetFontProperty, MultiEditOption, MultiEditChangeOption, ReadOnlyRenderType };
